// Shared PDF frame for Commission / Incentive / Extra slips. Lifts the
// header / meta-bar / status-stamp / signatures / footer out of
// commissionSlips.jsx so the same look applies across every slip type.
//
// Body content is type-specific and passed in as children. Render this
// inside an off-screen wrapper, capture with html2canvas, then jsPDF.

import { forwardRef } from 'react';

const formatRM = (amount) => `RM ${new Intl.NumberFormat('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0)}`;
const formatDate = (dateStr) => dateStr
    ? new Date(dateStr).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

const SlipPdfFrame = forwardRef(function SlipPdfFrame(
    { title, agentName, period, dateIssued, totalPayout, status, sectionTitle, children, preparedBy = 'Woo Jia Chee', approvedBy = 'Tan Jin Sheng' },
    ref,
) {
    return (
        <div ref={ref} style={{ width: '794px', fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#1a1a2e', background: '#fff', position: 'relative' }}>
            {/* Header */}
            <div style={{ background: '#fff', padding: '24px 32px 20px', display: 'flex', alignItems: 'center', gap: '16px', borderBottom: '3px solid #0a7cf8' }}>
                <img src="/nexwaveLogo.png" alt="nexwave" style={{ width: '48px', height: '48px', objectFit: 'contain' }} />
                <div style={{ width: '1px', height: '44px', background: '#e2e8f0' }} />
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '26px', fontWeight: 800, color: '#0a7cf8', letterSpacing: '-0.5px', lineHeight: 1 }}>nexwave</div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px', lineHeight: 1.4 }}>
                        by <strong style={{ fontWeight: 600, color: '#64748b' }}>Webbey Sdn Bhd</strong> &nbsp;&middot;&nbsp; 199301017370 (272108-A)<br />
                        71-2, Jln Macalister, 10400 George Town, Pulau Pinang
                    </div>
                </div>
                <div style={{ background: '#0a7cf8', borderRadius: '7px', padding: '9px 18px', textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>{title}</div>
                </div>
            </div>

            {/* Status stamp — colour by status. Pending shows "PENDING APPROVAL" in amber. */}
            {status && (() => {
                const cfg = status === 'Approved'
                    ? { border: '#065F46', color: '#065F46', bg: 'rgba(209,250,229,0.9)', label: 'Approved' }
                  : status === 'Pending'
                    ? { border: '#92400E', color: '#92400E', bg: 'rgba(254,243,199,0.9)', label: 'Pending Approval' }
                    : { border: '#991B1B', color: '#991B1B', bg: 'rgba(254,226,226,0.9)', label: status };
                return (
                    <div style={{
                        position: 'absolute', top: '24px', right: '200px', padding: '4px 14px', borderRadius: '5px',
                        fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
                        border: '2px solid',
                        borderColor: cfg.border,
                        color: cfg.color,
                        backgroundColor: cfg.bg,
                    }}>
                        {cfg.label}
                    </div>
                );
            })()}

            {/* Meta bar */}
            <div style={{ background: '#f8fafc', borderBottom: '1px solid #e8eef5', padding: '14px 32px', display: 'flex', gap: '36px', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '8.5px', textTransform: 'uppercase', letterSpacing: '0.9px', color: '#94a3b8', fontWeight: 600 }}>Agent Name</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>{agentName || '—'}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '8.5px', textTransform: 'uppercase', letterSpacing: '0.9px', color: '#94a3b8', fontWeight: 600 }}>Period</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>{period || '—'}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '8.5px', textTransform: 'uppercase', letterSpacing: '0.9px', color: '#94a3b8', fontWeight: 600 }}>Date Issued</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>{formatDate(dateIssued)}</span>
                </div>
                <div style={{ marginLeft: 'auto', background: '#0a7cf8', borderRadius: '8px', padding: '10px 20px', textAlign: 'right' }}>
                    <span style={{ fontSize: '8.5px', textTransform: 'uppercase', letterSpacing: '0.9px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block' }}>Total Payout</span>
                    <span style={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>{formatRM(totalPayout)}</span>
                </div>
            </div>

            {/* Section header */}
            <div style={{ padding: '16px 32px 10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h2 style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px', color: '#0a7cf8', whiteSpace: 'nowrap', margin: 0 }}>{sectionTitle || 'Breakdown'}</h2>
                <div style={{ flex: 1, height: '1px', background: '#e8eef5' }} />
            </div>

            {/* Body — type-specific */}
            <div style={{ padding: '0 32px 24px' }}>{children}</div>

            {/* Signatures */}
            <div style={{ padding: '16px 32px 10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h2 style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px', color: '#0a7cf8', whiteSpace: 'nowrap', margin: 0 }}>Authorisation</h2>
                <div style={{ flex: 1, height: '1px', background: '#e8eef5' }} />
            </div>
            <div style={{ padding: '18px 32px 28px', display: 'flex', gap: '40px' }}>
                <div style={{ flex: 1, borderTop: '2px solid #0a7cf8', paddingTop: '10px' }}>
                    <div style={{ fontWeight: 700, fontSize: '12px', color: '#1e293b' }}>{preparedBy}</div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px' }}>Prepared by</div>
                    <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 500, marginTop: '1px' }}>Accounting</div>
                    <div style={{ fontSize: '9.5px', color: '#94a3b8', marginTop: '7px' }}>Date: {formatDate(dateIssued)}</div>
                </div>
                <div style={{ flex: 1, borderTop: '2px solid #0a7cf8', paddingTop: '10px' }}>
                    <div style={{ fontWeight: 700, fontSize: '12px', color: '#1e293b' }}>{approvedBy}</div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px' }}>Approved by</div>
                    <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 500, marginTop: '1px' }}>Director</div>
                    <div style={{ fontSize: '9.5px', color: '#94a3b8', marginTop: '7px' }}>Date: {formatDate(dateIssued)}</div>
                </div>
            </div>

            {/* Footer */}
            <div style={{ background: '#f8fafc', borderTop: '1px solid #e8eef5', padding: '10px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#0a7cf8' }}>nexwave</span>
                <span style={{ fontSize: '9px', color: '#94a3b8' }}>This is a computer-generated document &nbsp;&middot;&nbsp; Webbey Sdn Bhd 199301017370 (272108-A)</span>
            </div>
        </div>
    );
});

export default SlipPdfFrame;
export { formatRM, formatDate };
